import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Switch,
  CircularProgress,
} from '@mui/material';
import { X, Info, AlertTriangle } from 'lucide-react';
import { useCommercialPlan } from 'contexts';
import { ICommercialPlanViewModel } from 'viewModels';
import { IUpdateCommercialPlanInputModel } from 'inputModels';
import { formatCurrency } from 'utils/formatCurrency';
import { CurrencyInput } from 'components';
import TransferUsersModal from './TransferUsersModal';
import {
  ModalWrapper,
  ModalHeader,
  ModalTitleContainer,
  ModalTitle,
  ModalDescription,
  ModalContent,
  ModalFooter,
  CloseButton,
  FormGroup,
  FormSection,
  FormSectionTitle,
  FormGrid,
  FormGridThree,
  FormLayout,
  SwitchContainer,
  SwitchItem,
  SwitchLabel,
  InfoAlert,
  WarningAlert,
  ErrorAlert,
  LoadingContainer,
  LoadingText,
} from './modalStyles';

interface EditPlanModalProps {
  open: boolean;
  onClose: () => void;
  plan: ICommercialPlanViewModel;
  onSuccess: () => void;
}

const EditPlanModal: React.FC<EditPlanModalProps> = ({
  open,
  onClose,
  plan,
  onSuccess,
}) => {
  const { handleUpdatePlan } = useCommercialPlan();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [pendingDeactivation, setPendingDeactivation] = useState(false);

  const [formData, setFormData] =
    useState<IUpdateCommercialPlanInputModel>({
      name: '',
      description: '',
      isActive: true,
      isDefault: false,
      minimumConsumption: 0,
      maxPublicFonts: 0,
      extraFontsTaxRate: 0,
    });

  useEffect(() => {
    if (plan) {
      setFormData({
        name: plan.name,
        description: plan.description || '',
        isActive: plan.isActive,
        isDefault: plan.isDefault,
        minimumConsumption: plan.minimumConsumption,
        maxPublicFonts: plan.maxPublicFonts,
        extraFontsTaxRate: plan.extraFontsTaxRate,
      });
    }
  }, [plan]);

  const handleInputChange = (
    field: keyof IUpdateCommercialPlanInputModel,
    value: any
  ) => {
    setFormData((prev: IUpdateCommercialPlanInputModel) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Nome é obrigatório');
      return;
    }

    if (formData.minimumConsumption < 0) {
      setError('Consumo mínimo deve ser maior ou igual a zero');
      return;
    }

    if (formData.maxPublicFonts < 0) {
      setError(
        'Quantidade máxima de fontes deve ser maior ou igual a zero'
      );
      return;
    }

    if (
      formData.extraFontsTaxRate < 0 ||
      formData.extraFontsTaxRate > 100
    ) {
      setError('Taxa de fontes extras deve estar entre 0 e 100');
      return;
    }

    // Impedir desativação do plano padrão
    if (plan.isDefault && !formData.isActive) {
      setError('Não é possível desativar o plano padrão. Defina outro plano como padrão primeiro.');
      return;
    }

    // Verificar se está tentando desativar um plano com usuários
    if (plan.isActive && !formData.isActive && plan.userCount > 0) {
      setPendingDeactivation(true);
      setShowTransferModal(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await handleUpdatePlan(plan.id, formData);
      if (success) {
        onSuccess();
      } else {
        setError('Erro ao atualizar plano');
      }
    } catch (err) {
      setError('Erro ao atualizar plano');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setPendingDeactivation(false);
      onClose();
    }
  };

  const handleTransferSuccess = async () => {
    setShowTransferModal(false);
    setPendingDeactivation(false);

    // Agora pode prosseguir com a desativação do plano
    setLoading(true);
    setError(null);

    try {
      const success = await handleUpdatePlan(plan.id, formData);
      if (success) {
        onSuccess();
      } else {
        setError('Erro ao atualizar plano');
      }
    } catch (err) {
      setError('Erro ao atualizar plano');
    } finally {
      setLoading(false);
    }
  };

  const handleTransferCancel = () => {
    setShowTransferModal(false);
    setPendingDeactivation(false);
    // Reverte a mudança do switch
    setFormData(prev => ({
      ...prev,
      isActive: true
    }));
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        disableScrollLock={false}
        keepMounted={false}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <ModalWrapper>
          <ModalHeader>
            <ModalTitleContainer>
              <ModalTitle id="modal-title">
                Editar Plano Comercial
              </ModalTitle>
              <ModalDescription id="modal-description">
                Edite as informações do plano "{plan?.name}"
              </ModalDescription>
            </ModalTitleContainer>
            <CloseButton onClick={handleClose}>
              <X size={20} />
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            {error && (
              <ErrorAlert>
                <AlertTriangle size={16} />
                {error}
              </ErrorAlert>
            )}

            <FormLayout>
              {/* Coluna Principal - Formulário */}
              <div>
                <FormSection>
                  <FormSectionTitle>
                    Informações Básicas
                  </FormSectionTitle>

                  <FormGroup>
                    <TextField
                      label="Nome do Plano"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange('name', e.target.value)
                      }
                      fullWidth
                      required
                      disabled={loading}
                      size="small"
                    />
                  </FormGroup>

                  <FormGroup>
                    <TextField
                      label="Descrição"
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange('description', e.target.value)
                      }
                      fullWidth
                      multiline
                      rows={3}
                      disabled={loading}
                      size="small"
                      placeholder="Descreva o propósito e características do plano"
                      slotProps={{
                        htmlInput: {
                          maxLength: 400,
                        },
                      }}
                    />
                  </FormGroup>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>
                    Configurações de Consumo
                  </FormSectionTitle>

                  <FormGridThree>
                    <FormGroup>
                      <CurrencyInput
                        label="Consumo Mínimo (R$)"
                        value={formData.minimumConsumption}
                        onChange={(value) => handleInputChange('minimumConsumption', value)}
                        disabled={loading}
                        helperText="Valor mínimo"
                      />
                    </FormGroup>

                    <FormGroup>
                      <TextField
                        label="Máx. Fontes Públicas"
                        type="text"
                        value={formData.maxPublicFonts}
                        onChange={(e) =>
                          handleInputChange(
                            'maxPublicFonts',
                            parseInt(e.target.value || '0')
                          )
                        }
                        fullWidth
                        disabled={loading}
                        inputMode="decimal"
                        inputProps={{ min: 0 }}
                        size="small"
                        helperText="Limite de fontes"

                      />
                    </FormGroup>

                    <FormGroup>
                      <TextField
                        label="Taxa de Fontes Extras (%)"
                        type="text"
                        inputMode="decimal"
                        value={formData.extraFontsTaxRate}
                        onChange={(e) =>
                          handleInputChange(
                            'extraFontsTaxRate',
                            parseFloat(e.target.value || '0')
                          )
                        }
                        fullWidth
                        disabled={loading}
                        inputProps={{ min: 0, max: 100, step: 0.1 }}
                        size="small"
                        helperText="Taxa adicional"
                      />
                    </FormGroup>
                  </FormGridThree>
                </FormSection>

                <FormSection>
                  <FormSectionTitle>Status do Plano</FormSectionTitle>
                  {formData.isDefault && (
                    <InfoAlert>
                      <Info size={16} />
                      Este plano será definido como padrão para novos
                      usuários
                    </InfoAlert>
                  )}
                  <SwitchContainer>
                    <SwitchItem>
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) =>
                          handleInputChange(
                            'isActive',
                            e.target.checked
                          )
                        }
                        disabled={loading || plan.isDefault}
                        size="small"
                      />
                      <SwitchLabel>
                        Plano Ativo
                        {plan.isDefault && (
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#6b7280',
                            marginLeft: '4px',
                            fontStyle: 'italic'
                          }}>
                            (não pode ser desativado)
                          </span>
                        )}
                      </SwitchLabel>
                    </SwitchItem>

                    <SwitchItem>
                      <Switch
                        checked={formData.isDefault}
                        onChange={(e) =>
                          handleInputChange(
                            'isDefault',
                            e.target.checked
                          )
                        }
                        disabled={loading || plan.isDefault || !formData.isActive}
                        size="small"
                      />
                      <SwitchLabel>Plano Padrão</SwitchLabel>
                    </SwitchItem>
                  </SwitchContainer>

                  {plan.isDefault && (
                    <WarningAlert>
                      <AlertTriangle size={16} />
                      <div>
                        <strong>Plano Padrão:</strong> Este plano não pode ser desativado ou excluído.
                        Defina outro plano como padrão primeiro.
                      </div>
                    </WarningAlert>
                  )}

                  {plan.isActive && !formData.isActive && plan.userCount > 0 && (
                    <WarningAlert>
                      <AlertTriangle size={16} />
                      <div>
                        <strong>Atenção:</strong> Este plano possui {plan.userCount} usuário{plan.userCount !== 1 ? 's' : ''} ativo{plan.userCount !== 1 ? 's' : ''}.
                        Será necessário transferir os usuários para outro plano antes de desativar.
                      </div>
                    </WarningAlert>
                  )}

                  {formData.extraFontsTaxRate > 0 && (
                    <WarningAlert>
                      <AlertTriangle size={16} />
                      Taxa de {formData.extraFontsTaxRate}% será
                      aplicada quando exceder o limite de{' '}
                      {formData.maxPublicFonts} fontes
                    </WarningAlert>
                  )}
                </FormSection>
              </div>

              {/* Coluna Lateral - Informações do Plano */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                }}
              >
                <FormSection
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <FormSectionTitle>
                    Informações do Plano
                  </FormSectionTitle>

                  <FormGroup>
                    <label
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      Consumo Mínimo Atual
                    </label>
                    <div
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px',
                      }}
                    >
                      {formatCurrency(plan.minimumConsumption)}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <label
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      Usuários Ativos
                    </label>
                    <div
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px',
                      }}
                    >
                      {plan.userCount} usuário
                      {plan.userCount !== 1 ? 's' : ''}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <label
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      Regras de Fontes
                    </label>
                    <div
                      style={{
                        fontSize: '1.125rem',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '16px',
                      }}
                    >
                      {plan.fontRuleCount} regra
                      {plan.fontRuleCount !== 1 ? 's' : ''}
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <label
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      Status Atual
                    </label>
                    <div
                      style={{
                        marginBottom: '16px',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: plan.isActive
                            ? '#dcfce7'
                            : '#fee2e2',
                          color: plan.isActive ? '#166534' : '#991b1b',
                        }}
                      >
                        {plan.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                      {plan.isDefault && (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: '#e0e7ff',
                            color: '#3730a3',
                            marginLeft: '8px',
                          }}
                        >
                          Padrão
                        </span>
                      )}
                    </div>
                  </FormGroup>

                  <FormGroup style={{ flex: 1 }}>
                    <label
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        marginBottom: '4px',
                      }}
                    >
                      Data de Criação
                    </label>
                    <div
                      style={{
                        fontSize: '0.875rem',
                        color: '#64748b',
                        fontStyle: 'italic',
                      }}
                    >
                      {new Date(plan.createdAt).toLocaleDateString(
                        'pt-BR',
                        {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        }
                      )}
                    </div>
                  </FormGroup>
                </FormSection>
              </div>
            </FormLayout>
          </ModalContent>

          <ModalFooter>
            <button
              onClick={handleClose}
              disabled={loading}
              style={{
                padding: '8px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                background: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
              }}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || !formData.name.trim()}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                background: '#293778',
                color: 'white',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: '500',
                opacity: loading || !formData.name.trim() ? 0.5 : 1,
              }}
            >
              {loading ? (
                <LoadingContainer>
                  <CircularProgress size={16} />
                  <LoadingText>Salvando...</LoadingText>
                </LoadingContainer>
              ) : (
                'Salvar Alterações'
              )}
            </button>
          </ModalFooter>
        </ModalWrapper>
      </Modal>
      <TransferUsersModal
        open={showTransferModal}
        onClose={handleTransferCancel}
        sourcePlan={plan}
        onSuccess={handleTransferSuccess}
      />
    </>

  );
};

export default EditPlanModal;
